// Static copy for the Talent Network landing page (/jobs/ai). Everything
// the ops team might tune (rates, domains, FAQ copy) lives here, separate
// from component logic.

import {getBrandName} from '../../components/Brand';

export const workTypes = [{
  title: 'Benchmarks & evals',
  description: 'Author the tasks, test cases, and gold answers that measure what frontier models can and can\'t do in your field.',
  domains: ['Python SWE', 'ML', 'Domain experts'],
  rate: '$60–150/hr',
}, {
  title: 'Model-in-the-loop data',
  description: 'Work alongside lab engineers in real time — reviewing model outputs, breaking edge cases, and shaping training data.',
  domains: ['Senior SWE', 'Medicine', 'Law', 'Finance'],
  rate: '$80–200/hr',
}, {
  title: 'Training data & preferences',
  description: 'Write, rank, and refine the expert demonstrations models learn from — the highest-leverage writing you\'ll ever do.',
  domains: ['All domains', 'Writing', 'Science'],
  rate: '$50–120/hr',
}, {
  title: 'Rubric design & QA',
  description: 'Define what "good" means: build grading rubrics, calibrate reviewers, and audit data quality for entire projects.',
  domains: ['Senior ICs', 'Managers', 'Educators'],
  rate: '$70–150/hr',
}];

export const heroStats = [
  {value: '$50–150+', label: 'Typical hourly rates'},
  {value: '5–40 hrs', label: 'Per week, you choose'},
  {value: '100%', label: 'Remote'},
  {value: '5 min', label: 'To apply'},
];

export const howItWorks = [{
  step: '01',
  title: 'Apply in 5 minutes',
  description: `Complete the job matching profile on your ${getBrandName()} account — domain, availability, and rate. No cover letter.`,
}, {
  step: '02',
  title: 'Join the waitlist',
  description: 'A complete profile puts you on the waitlist. There\'s nothing else to do — we\'ll email you when there\'s a next step.',
}, {
  step: '03',
  title: 'Get matched to projects',
  description: 'When a partner lab needs your profile, we reach out with scope, rate, and timeline. Accept what fits, skip what doesn\'t.',
}];

export const vettingPillars = [{
  title: 'Verified, not self-reported',
  description: 'We read your actual resume, so a profile that says "worked at Google" means it — not "wants to work at Google".',
}, {
  title: 'Track record you can\'t fake',
  description: `${getBrandName()} members bring years of interview practice, peer mocks, and community history — signal most talent pools never see.`,
}, {
  title: 'Quality that compounds',
  description: 'Every task is scored. Top contributors get first access to the best projects; quality below the bar means leaving the pool.',
}];

export const faqItems = [{
  question: 'What is human data work?',
  answer: 'AI labs need experts to create the material models learn from: benchmark tasks, evaluation rubrics, expert demonstrations, and reviews of model outputs. It\'s knowledge work — closer to teaching and editing than labeling.',
}, {
  question: 'Who are the partner labs?',
  answer: 'Frontier AI labs and enterprise AI teams. Specific names are shared once you\'re matched to a project and sign an NDA — confidentiality is part of the job.',
}, {
  question: 'How much can I earn?',
  answer: 'Rates depend on domain and seniority. Most software engineers land between $60 and $150 per hour; specialists like physicians and attorneys can earn more. Every project posts its rate up front.',
}, {
  question: 'How much time does it take?',
  answer: 'Most members contribute 5–20 hours a week alongside a full-time job. Some projects offer 35+ hour engagements for members who want full-time work.',
}, {
  question: 'Do I need AI experience?',
  answer: 'No. Labs want deep expertise in your field — we train you on the tools, data formats, and rubric-writing before your first project.',
}, {
  question: 'Can I do this alongside my full-time job?',
  answer: 'Usually, yes — engagements are remote and scheduled around you. Check your employer\'s moonlighting policy; work for a direct competitor of your employer is off the table.',
}, {
  question: 'How does vetting work?',
  answer: 'Two layers: a structured application scored on your practice history, peer reviews, and resume, then ongoing per-task quality scores once you\'re matched. This is how we keep the pool strong enough that labs come back.',
}];
